import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Modal, Input, Checkbox, Select, Progress, message, DatePicker } from 'antd';
import { getUserDetail } from '../util/util';
import { 
  ChevronLeft, 
  X, 
  Wallet, 
  TrendingUp, 
  ShieldCheck, 
  ArrowRight, 
  Trash2, 
  Plus,
  CheckCircle2
} from 'lucide-react';

// Import MVest hooks
import { 
  useCreateMVestAccount, 
  useAddMVestBeneficiary, 
  useDeleteMVestBeneficiary, 
  useGetMVestBeneficiaries, 
  useInitiateMVestPayment 
} from '../hooks/mvest/useMVest';

const modalResponsiveCSS = `
  .mvest-modal .ant-input:hover,
  .mvest-modal .ant-input:focus {
    border-color: #0D7C4B !important;
    box-shadow: 0 0 0 2px rgba(13, 124, 75, 0.2) !important;
  }
  .mvest-modal .ant-select-selector:hover,
  .mvest-modal .ant-select-focused .ant-select-selector {
    border-color: #0D7C4B !important;
    box-shadow: 0 0 0 2px rgba(13, 124, 75, 0.2) !important;
  }
  .mvest-modal .ant-picker:hover,
  .mvest-modal .ant-picker-focused {
    border-color: #0D7C4B !important;
    box-shadow: 0 0 0 2px rgba(13, 124, 75, 0.2) !important;
  }
  @media (max-width: 768px) {
    .mvest-modal .ant-modal {
      max-width: calc(100vw - 32px) !important;
      margin: 16px auto !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
    }
    .mvest-modal .ant-modal-content {
      border-radius: 24px !important;
    }
  }
`;

const { Option } = Select;

const MVestFlow = ({ visible, onClose }) => {
  const [step, setStep] = useState(1);
  const [showAddBeneficiary, setShowAddBeneficiary] = useState(false);
  
  // Form Data State
  const [formData, setFormData] = useState({
    contributionType: 'Monthly',
    amount: '',
    paymentMethod: 'Mobile Money'
  });

  // Local state for Add Beneficiary Form
  const [newBeneficiary, setNewBeneficiary] = useState({
    firstName: '',
    otherName: '',
    beneficiaryContact: '',
    birthDate: null,
    relation: '',
    percentageAllocation: ''
  });

  // --- API Hooks ---
  const { mutate: createMVestAccount, isLoading: isCreatingAccount } = useCreateMVestAccount();
  const { mutate: addBeneficiary, isLoading: isAddingBeneficiary } = useAddMVestBeneficiary();
  const { mutate: deleteBeneficiary } = useDeleteMVestBeneficiary();
  const user = getUserDetail();
  const { data: beneficiariesData } = useGetMVestBeneficiaries(visible, user?.member_number);
  const beneficiariesList = Array.isArray(beneficiariesData) ? beneficiariesData : [];
  const { mutate: initiatePayment, isLoading: isInitiatingPayment } = useInitiateMVestPayment();

  // Navigation logic
  const nextStep = () => setStep(prev => prev + 1);
  const prevStep = () => setStep(prev => prev - 1);

  // Calculate total allocation percentage
  const totalAllocation = beneficiariesList.reduce((sum, b) => sum + Number(b.allocation || 0), 0);

  const ModalHeader = ({ title, showBack = true, currentStep, totalSteps = 4 }) => (
    <div className="flex items-center justify-between w-full mb-6">
      <div className="flex items-center gap-3">
        {showBack && (
          <button onClick={prevStep} className="p-1 hover:bg-gray-100 rounded-full transition">
            <ChevronLeft size={24} className="text-gray-800" />
          </button>
        )}
        <div>
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          {currentStep && (
            <span className="text-emerald-500 text-sm font-medium">Step {currentStep} of {totalSteps}</span>
          )}
        </div>
      </div>
       <button onClick={onClose} className="btn-white p-2 rounded-full">
         <X size={20} className="text-gray-600" />
       </button>
    </div>
  );

  // --- API Actions ---
  const handleCreateAccount = () => {
    if (!formData.amount) return message.error("Please enter an amount");
    createMVestAccount({
      mvest_plan: formData.contributionType,
      monthly_contribution: formData.amount
    }, {
      onSuccess: () => nextStep(),
      onError: (err) => message.error(err?.message || "Failed to create account")
    });
  };

  const handleSaveBeneficiary = () => {
    if (!newBeneficiary.firstName.trim()) {
      return message.error('Please enter the first name');
    }
    if (!newBeneficiary.birthDate) {
      return message.error('Please select the birth date');
    }
    const age = dayjs().diff(newBeneficiary.birthDate, 'year');
    if (age < 16) {
      return message.error('Beneficiary must be at least 16 years old');
    }
    addBeneficiary({
      firstname: newBeneficiary.firstName,
      othername: newBeneficiary.otherName,
      beneficiaryContact: newBeneficiary.beneficiaryContact,
      birth_date: newBeneficiary.birthDate ? newBeneficiary.birthDate.format('YYYY-MM-DD') : '',
      relation: newBeneficiary.relation,
      percentageAllocation: newBeneficiary.percentageAllocation
    }, {
      onSuccess: () => {
        setShowAddBeneficiary(false);
        setNewBeneficiary({ firstName: '', otherName: '', beneficiaryContact: '', birthDate: null, relation: '', percentageAllocation: '' });
        message.success("Beneficiary added");
      },
      onError: (err) => message.error(err?.message || "Error adding beneficiary")
    });
  };

  const handlePayment = () => {
    initiatePayment({
      amount: formData.amount,
      currency: 'GHS'
    }, {
      onSuccess: () => nextStep(),
      onError: (err) => message.error(err?.message || "Payment initiation failed")
    });
  };

  // --- STEP RENDERERS ---

  const renderIntro = () => (
    <div className="p-2">
       <div className="flex justify-end mb-4">
         <button onClick={onClose} className="btn-white p-2 rounded-full"><X size={20}/></button>
       </div>
      <h1 className="text-2xl font-bold mb-2">MVest</h1>
      <p className="text-gray-500 mb-8">Build your wealth with a flexible investment plan tailored to your goals.</p>
      <div className="space-y-6 mb-8">
        {[
          { icon: <Wallet className="text-emerald-500" />, title: "Flexible Contributions", desc: "Contribute anytime with no fixed commitments" },
          { icon: <TrendingUp className="text-emerald-500" />, title: "Competitive Returns", desc: "Grow your money with strong consistent returns" },
          { icon: <ShieldCheck className="text-emerald-500" />, title: "Secure and regulated", desc: "Your funds protected under strict regulation" },
          { icon: <ArrowRight className="text-emerald-500" />, title: "Easy withdrawals", desc: "Access your money quickly whenever you need" },
        ].map((item, i) => (
          <div key={i} className="flex gap-4">
            <div className="mt-1">{item.icon}</div>
            <div>
              <h4 className="font-bold text-gray-900">{item.title}</h4>
              <p className="text-sm text-gray-500">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
       <button className="btn btn-primary w-full h-14 rounded-2xl font-bold" onClick={nextStep}>
         Start Investing
       </button>
    </div>
  );

  const renderSchemeDetails = () => (
    <div>
      <ModalHeader title="Scheme details" currentStep={1} />
      <p style={{ color: '#666', fontSize: 14, marginBottom: 20 }}>Choose how you'd like to contribute to your MVest plan.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {['Monthly', 'Yearly', 'Lump Sum'].map(option => {
          const isSelected = formData.contributionType === option;
          return (
            <div
              key={option}
              onClick={() => setFormData({ ...formData, contributionType: option })}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '16px',
                borderRadius: 12,
                border: isSelected ? '1.5px solid #0D7C4B' : '1.5px solid #e5e7eb',
                background: isSelected ? '#f0fdf4' : '#fff',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <div style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                border: isSelected ? '5px solid #0D7C4B' : '2px solid #9ca3af',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }} />
              <span style={{ fontSize: 15, fontWeight: 500, color: '#111' }}>{option}</span>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 24 }}>
        <Input
          size="large"
          placeholder="Contribution Amount in GH₵"
          style={{ height: 56, borderRadius: 12, fontSize: 15 }}
          value={formData.amount}
          onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
        />
      </div>
      <button
        onClick={handleCreateAccount}
        disabled={isCreatingAccount}
        style={{
          width: '100%',
          height: 56,
          marginTop: 32,
          borderRadius: 9999,
          border: 'none',
          background: 'linear-gradient(90deg, #0D7C4B 0%, #10b981 100%)',
          color: '#fff',
          fontSize: 16,
          fontWeight: 600,
          cursor: isCreatingAccount ? 'not-allowed' : 'pointer',
          opacity: isCreatingAccount ? 0.6 : 1
        }}
      >
        {isCreatingAccount ? 'Creating...' : 'Continue'}
      </button>
    </div>
  );

  const renderBeneficiaries = () => (
    <div>
      <ModalHeader title="Beneficiaries" currentStep={2} />
      <Progress percent={totalAllocation} showInfo={false} strokeColor="#10b981" trailColor="#e5e7eb" strokeWidth={4} />
      <div className="text-[10px] uppercase font-bold text-gray-400 mt-4 mb-6">Allocated {totalAllocation}% of 100%</div>
      
      <div className="space-y-4 mb-8 max-h-60 overflow-y-auto">
        {beneficiariesList.map((b) => (
          <div key={b.id} className="p-4 rounded-2xl border border-gray-100 bg-white shadow-sm flex justify-between items-center">
            <div>
              <span className="font-bold text-gray-900">{b.first_name} {b.last_name}</span>
              <div className="text-xs text-gray-500 mt-1">{b.relationship} • <span className="text-emerald-500 font-bold">{b.allocation}%</span></div>
            </div>
            <button onClick={() => deleteBeneficiary(b.beneficiaryContact || b.contact || b.phone || b.id)} className="text-gray-400 hover:text-red-500 transition"><Trash2 size={16}/></button>
          </div>
        ))}
        <button onClick={() => setShowAddBeneficiary(true)} className="w-full py-4 border-2 border-dashed border-gray-200 rounded-2xl text-gray-500 font-bold flex items-center justify-center gap-2 hover:bg-gray-50 transition">
          <Plus size={20} /> Add Beneficiary
        </button>
      </div>

       <button className="btn btn-primary w-full h-14 rounded-2xl font-bold disabled:opacity-50" onClick={nextStep} disabled={totalAllocation !== 100}>
         Continue
       </button>

       <Modal open={showAddBeneficiary} footer={null} closable={false} width={400} centered zIndex={5000} style={{ padding: '16px' }} bodyStyle={{ padding: '24px', borderRadius: '16px' }} className="mvest-modal">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold">Add Beneficiary</h3>
           <button onClick={() => setShowAddBeneficiary(false)} className="btn-white p-2 rounded-full"><X size={16}/></button>
        </div>
        <div className="space-y-4">
          <Input placeholder="First Name" className="h-12 rounded-xl" value={newBeneficiary.firstName} onChange={e => setNewBeneficiary({...newBeneficiary, firstName: e.target.value})} />
          <Input placeholder="Other Name" className="h-12 rounded-xl" value={newBeneficiary.otherName} onChange={e => setNewBeneficiary({...newBeneficiary, otherName: e.target.value})} />
          <Input placeholder="Beneficiary Contact" className="h-12 rounded-xl" value={newBeneficiary.beneficiaryContact} onChange={e => setNewBeneficiary({...newBeneficiary, beneficiaryContact: e.target.value})} />
           <DatePicker placeholder="Birth Date" className="w-full h-12 rounded-xl" value={newBeneficiary.birthDate} disabledDate={d => d && d.isAfter(dayjs().subtract(16, 'year'), 'day')} onChange={val => setNewBeneficiary({...newBeneficiary, birthDate: val})} />
          <Select placeholder="Relation" className="w-full h-12" size="large" value={newBeneficiary.relation} onChange={val => setNewBeneficiary({...newBeneficiary, relation: val})}>
            <Option value="Spouse">Spouse</Option>
            <Option value="Child">Child</Option>
            <Option value="Parent">Parent</Option>
          </Select>
          <Input placeholder="Allocation(%)" type="number" className="h-12 rounded-xl" value={newBeneficiary.percentageAllocation} onChange={e => setNewBeneficiary({...newBeneficiary, percentageAllocation: e.target.value})} />
           <button className="btn btn-primary w-full h-12 rounded-xl font-bold disabled:opacity-50" onClick={handleSaveBeneficiary} disabled={isAddingBeneficiary}>
             {isAddingBeneficiary ? 'Saving...' : 'Save Beneficiary'}
           </button>
        </div>
      </Modal>
    </div>
  );

  const renderReview = () => (
    <div>
      <ModalHeader title="Review" currentStep={3} />
      <div className="bg-gray-50/50 rounded-2xl p-6 border border-gray-100 mb-8">
        <h4 className="font-bold mb-6">Payment Summary</h4>
        <div className="space-y-4 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">Plan</span><span className="font-bold">{formData.contributionType}</span></div>
          <div className="flex justify-between border-b pb-4"><span className="text-gray-500">Amount</span><span className="font-bold">GH₵ {formData.amount}</span></div>
          <div className="pt-2">
            <h5 className="font-bold mb-4">Beneficiaries</h5>
            {beneficiariesList.map(b => (
               <div key={b.id} className="flex justify-between text-gray-500 mb-2"><span>{b.first_name}</span><span className="font-bold text-gray-900">{b.allocation}%</span></div>
            ))}
          </div>
        </div>
      </div>
      <Checkbox className="mb-10 text-sm">I accept the terms and conditions of the MVest investment.</Checkbox>
       <button className="btn btn-primary w-full h-14 rounded-2xl font-bold flex items-center justify-center gap-2" onClick={nextStep}>
         Proceed to payment <ArrowRight size={18} />
       </button>
    </div>
  );

  const renderPayment = () => (
    <div>
      <ModalHeader title="Payment" currentStep={4} />
      <div className="bg-gray-50/50 rounded-2xl p-6 mb-8 flex flex-col gap-4">
        <div className="flex justify-between items-center"><span className="text-gray-500 text-sm">Amount</span><span className="font-bold text-emerald-600">GH₵ {formData.amount}</span></div>
      </div>
      <h4 className="font-bold text-sm mb-4">Payment Method</h4>
      <div className="p-4 rounded-2xl border border-emerald-500 bg-emerald-50/30 flex items-center justify-between mb-24">
        <div className="flex flex-col"><span className="font-bold">Mobile Money</span><span className="text-xs text-gray-500">MTN, Vodafone, or AirtelTigo</span></div>
        <div className="w-5 h-5 rounded-full border-4 border-emerald-600 bg-white"></div>
      </div>
       <button className="btn btn-primary w-full h-14 rounded-2xl font-bold disabled:opacity-50" onClick={handlePayment} disabled={isInitiatingPayment}>
         {isInitiatingPayment ? 'Processing...' : 'Confirm and Pay'} <ArrowRight size={18} />
       </button>
    </div>
  );

  const renderSuccess = () => (
    <div className="text-center py-6">
      <div className="flex justify-center mb-8">
        <div className="w-32 h-32 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg">
          <CheckCircle2 size={72} className="text-white" />
        </div>
      </div>
      <h2 className="text-2xl font-bold mb-2">Investment created!</h2>
      <p className="text-gray-500 mb-8">Your payment is being processed.</p>
       <button className="btn btn-primary w-full h-14 rounded-2xl font-bold" onClick={onClose}>
         Go to Dashboard
       </button>
    </div>
  );

   return (
     <>
       <style>{modalResponsiveCSS}</style>
       <Modal 
         open={visible} 
         footer={null} 
         closable={false} 
         width={450} 
         centered 
         zIndex={5000}
         style={{ padding: '16px' }}
         bodyStyle={{ padding: '24px', borderRadius: '32px' }}
         className="mvest-modal"
       >
       {step === 1 && renderIntro()}
       {step === 2 && renderSchemeDetails()}
       {step === 3 && renderBeneficiaries()}
       {step === 4 && renderReview()}
        {step === 5 && renderPayment()}
        {step === 6 && renderSuccess()}
      </Modal>
      </>
    );
  };

export default MVestFlow;